import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, CreditCard, Search, Copy, Upload, BarChart3 } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import type { SubscriptionPlan, PlanStatus, PlanType } from '@/types/subscription';

export const SubscriptionPlanListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPlanType, setFilterPlanType] = useState<string>('all');
  const [filterTrialEnabled, setFilterTrialEnabled] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('displayOrder');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedPlans, setSelectedPlans] = useState<Set<string>>(new Set());
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'subscriptionPlan');
  const canEdit = hasPermission('edit', 'subscriptionPlan');
  const canDelete = hasPermission('delete', 'subscriptionPlan');
  const canActivate = hasPermission('activate', 'subscriptionPlan');

  // Build fetch function with filters and sorting
  const fetchPlansFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      return subscriptionPlanApi.list({
        page: params.page,
        size: params.size,
        sort: `${sortBy},${sortDirection}`,
        status: filterStatus !== 'all' ? (filterStatus as PlanStatus) : undefined,
        planType: filterPlanType !== 'all' ? (filterPlanType as PlanType) : undefined,
        trialEnabled: filterTrialEnabled !== 'all' ? filterTrialEnabled === 'true' : undefined,
        search: searchQuery.trim() || undefined,
      });
    };
  }, [searchQuery, filterStatus, filterPlanType, filterTrialEnabled, sortBy, sortDirection]);

  // Use paginated list hook
  const {
    data: plans,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<SubscriptionPlan>({
    fetchFn: fetchPlansFn,
    entityName: 'subscription plans',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (planId: string) => {
      await subscriptionPlanApi.delete(planId);
    },
    entityName: 'subscription plan',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  // Bulk operations mutation
  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, action }: { ids: string[]; action: 'activate' | 'deactivate' | 'archive' }) => {
      const promises = ids.map((id) => {
        if (action === 'activate') {
          return subscriptionPlanApi.activate(id);
        } else if (action === 'deactivate') {
          return subscriptionPlanApi.deactivate(id);
        } else {
          return subscriptionPlanApi.archive(id);
        }
      });
      await Promise.all(promises);
    },
    entityName: 'subscription plans',
    operation: 'update',
    successMessage: (_data, variables) => `${variables.ids.length} plan(s) ${variables.action}d`,
    onSuccess: () => {
      setSelectedPlans(new Set());
      refetch();
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: async (planId: string) => {
      return subscriptionPlanApi.clone(planId);
    },
    entityName: 'subscription plan',
    operation: 'create',
    successMessage: 'Plan cloned successfully',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (planId: string) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    await deleteMutation.mutate(planId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleBulkStatusChange = async (action: 'activate' | 'deactivate' | 'archive') => {
    if (selectedPlans.size === 0) return;
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${selectedPlans.size} plan(s)?`)) return;
    await bulkStatusMutation.mutateAsync({ ids: Array.from(selectedPlans), action });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const toggleSelect = (planId: string) => {
    const newSelected = new Set(selectedPlans);
    if (newSelected.has(planId)) {
      newSelected.delete(planId);
    } else {
      newSelected.add(planId);
    }
    setSelectedPlans(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedPlans.size === plans.length) {
      setSelectedPlans(new Set());
    } else {
      setSelectedPlans(new Set(plans.map((p) => p.id)));
    }
  };

  const getStatusColor = (status: PlanStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'ARCHIVED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Subscription Plans</h1>
          <p className="text-muted-foreground mt-2">Manage subscription plans</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <>
              <Button asChild variant="outline">
                <Link to="/admin/subscription-plans/import">
                  <Upload className="mr-2 h-4 w-4" />
                  Import
                </Link>
              </Button>
              <Button asChild>
                <Link to="/admin/subscription-plans/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Plan
                </Link>
              </Button>
            </>
          )}
          <Button asChild variant="outline">
            <Link to="/admin/subscription-plans/stats">
              <BarChart3 className="mr-2 h-4 w-4" />
              Statistics
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPlanType} onValueChange={setFilterPlanType}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="FREE">Free</SelectItem>
                <SelectItem value="BASIC">Basic</SelectItem>
                <SelectItem value="PREMIUM">Premium</SelectItem>
                <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                <SelectItem value="PRO">Pro</SelectItem>
                <SelectItem value="CUSTOM">Custom</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTrialEnabled} onValueChange={setFilterTrialEnabled}>
              <SelectTrigger>
                <SelectValue placeholder="Trial" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Trial Enabled</SelectItem>
                <SelectItem value="false">No Trial</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedPlans.size > 0 && canActivate && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {selectedPlans.size} plan(s) selected
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkStatusChange('activate')}
                  disabled={bulkStatusMutation.isLoading}
                >
                  Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkStatusChange('deactivate')}
                  disabled={bulkStatusMutation.isLoading}
                >
                  Deactivate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkStatusChange('archive')}
                  disabled={bulkStatusMutation.isLoading}
                >
                  Archive
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedPlans(new Set())}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle>Plans ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all subscription plans</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading subscription plans..." />
          ) : (error || plans.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load subscription plans. Please try again or create a new one." : "Get started by creating your first subscription plan."}
              icon={CreditCard}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/subscription-plans/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {canActivate && (
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedPlans.size === plans.length && plans.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300"
                        />
                      </TableHead>
                    )}
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('planCode')}
                    >
                      Plan Code
                      {sortBy === 'planCode' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('displayName')}
                    >
                      Display Name
                      {sortBy === 'displayName' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Plan Type</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortBy === 'status' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('monthlyPrice')}
                    >
                      Monthly Price
                      {sortBy === 'monthlyPrice' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('yearlyPrice')}
                    >
                      Yearly Price
                      {sortBy === 'yearlyPrice' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Trial</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan.id}>
                      {canActivate && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedPlans.has(plan.id)}
                            onChange={() => toggleSelect(plan.id)}
                            className="rounded border-gray-300"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">{plan.planCode}</TableCell>
                      <TableCell className="font-medium">{plan.displayName}</TableCell>
                      <TableCell>{plan.planType}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(plan.status)}`}>
                          {plan.status}
                        </span>
                      </TableCell>
                      <TableCell>{formatPrice(plan.monthlyPrice, plan.currency)}</TableCell>
                      <TableCell>{formatPrice(plan.yearlyPrice, plan.currency)}</TableCell>
                      <TableCell>
                        {plan.trialEnabled ? (
                          <span className="text-green-600 dark:text-green-400">
                            {plan.trialDurationDays ? `${plan.trialDurationDays}d` : 'Yes'}
                          </span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/subscription-plans/${plan.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/subscription-plans/${plan.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={async () => {
                                await cloneMutation.mutateAsync(plan.id);
                              }}
                              disabled={cloneMutation.isLoading}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(plan.id)}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                    </div>
                    <Select value={pagination.size.toString()} onValueChange={(v) => setSize(parseInt(v))}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionPlanListPage;
